import { useState } from 'react'
import { Users } from 'lucide-react'
import { teams as teamApi } from '@/api/endpoints'
import type { Team, TeamInvitation, TeamMember } from '@/api/types'
import { Err } from '@/components/Err'
import { ToneBadge } from '@/components/ToneBadge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Empty, PageHead, Section } from '@/components/layout/Page'
import { teamRoleWord } from '@/domain/words'
import { useInvites } from '@/hooks/useInvites'
import { usePoll } from '@/hooks/usePoll'
import { useSession } from '@/hooks/useSession'
import { byTeamRole, MEMBER_ROW } from '../memberRows'
import { ACTION, ACTION_DANGER, CHIP } from '../vm/chips'

// 사용자의 팀 화면(F-92). 내가 든 팀의 목록이고, 내게 온 초대가 그 위에 `수락`·`거절` 로 선다.
// 내가 관리자인 팀에서는 멤버를 빼고(`빼기`), 새 사람은 아이디로 초대해 받은 사람이 수락해야 든다 —
// 일반 사용자인 팀 관리자는 사람을 바로 넣지 못한다(백엔드가 403 `invite_required` 로 거절한다).
// 답을 기다리는 초대는 그 팀 안에서 `거두기` 로 거둔다.
//
// 팀은 표시 이름으로, 사람은 아이디로만 부른다 — 팀의 식별자와 내부 id 는 이 화면에 없다.
// **자기 줄과 마지막 관리자의 줄에는 `빼기` 가 없다** — 자기를 빼면 그 팀을 관리하는 자리를 잃고,
// 마지막 관리자는 서버가 거절한다(화면이 먼저 안다).
// 쓰기를 마치면 그 목록을 곧바로 다시 읽는다 — 변경 스트림은 최고 관리자의 것이라 다음 폴링까지 옛 값이 남는다.
// **답의 거절은 이 화면이 든다** — 마지막 초대의 답이 거절되면 다시 읽은 목록이 비어 `받은 초대` 절이 사라지는데,
// 거절을 그 절이 들면 원인을 말하는 문장도 함께 사라져 초대가 이유 없이 없어진 것으로 보인다.

const TEAMS_POLL_MS = 10000
const TEAM_INVITES_POLL_MS = 15000

// 같은 사유라도 이 칸에서 할 수 있는 것은 아이디를 고치는 것이다.
const INVITE_REASONS: Readonly<Record<string, string>> = {
  user_not_found: '그 아이디의 사용자가 없습니다. 아이디를 확인하세요.',
}

export default function MyTeams() {
  const teams = usePoll(teamApi.mine, TEAMS_POLL_MS)
  const invites = useInvites()
  const [answerError, setAnswerError] = useState<unknown>(null)
  const list = teams.data ?? []
  const waiting = invites.list ?? []

  return (
    <>
      <PageHead icon={<Users className="size-5" />} title="팀" facts={teams.data ? `${list.length}개` : ''} />

      {waiting.length > 0 || answerError != null ? (
        <Received
          invitations={waiting}
          error={answerError}
          onError={setAnswerError}
          onAnswered={() => {
            invites.refresh()
            teams.refresh()
          }}
        />
      ) : null}

      <Section title="전체">
        <Err error={teams.error} />
        {list.length === 0 ? (
          <Empty>
            <Users className="size-4" /> {teams.loading && !teams.data ? '불러오는 중' : '팀 없음'}
          </Empty>
        ) : (
          <div className="grid gap-2">
            {list.map((t) => (
              <TeamCard key={t.id} team={t} onChanged={teams.refresh} />
            ))}
          </div>
        )}
      </Section>
    </>
  )
}

/** 내게 온 초대. 한 번에 하나씩 답하고, 답한 뒤에는 초대와 팀 목록을 함께 다시 읽는다. 거절은 다음 답까지 남는다. */
function Received({
  invitations,
  error,
  onError,
  onAnswered,
}: {
  invitations: TeamInvitation[]
  error: unknown
  onError: (err: unknown) => void
  onAnswered: () => void
}) {
  const [busy, setBusy] = useState(false)
  const answer = async (inv: TeamInvitation, accept: boolean) => {
    setBusy(true)
    onError(null)
    try {
      await (accept ? teamApi.accept(inv.id) : teamApi.decline(inv.id))
    } catch (err) {
      onError(err)
    }
    setBusy(false)
    onAnswered()
  }
  return (
    <Section title="받은 초대">
      <div className="grid gap-2">
        <Err error={error} />
        {invitations.map((inv) => (
          <div key={inv.id} className="flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-lg border bg-card px-4 py-3">
            <Users className="size-4 flex-none text-brand-hover" />
            <span className="min-w-0 truncate font-medium">{inv.team_display_name}</span>
            <span className="text-[12px] text-muted-foreground">초대한 사람 {inv.invited_by}</span>
            <span className="ml-auto flex flex-wrap items-center gap-1.5">
              <button type="button" className={CHIP} disabled={busy} onClick={() => void answer(inv, true)}>
                수락
              </button>
              <button type="button" className={CHIP} disabled={busy} onClick={() => void answer(inv, false)}>
                거절
              </button>
            </span>
          </div>
        ))}
      </div>
    </Section>
  )
}

/** 팀 하나 — 멤버 줄, 그리고 내가 관리자면 빼기와 초대. */
function TeamCard({ team, onChanged }: { team: Team; onChanged: () => void }) {
  const me = useSession()
  const manages = team.my_role === 'owner'
  const owners = team.members.filter((m) => m.role === 'owner').length
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const removable = (m: TeamMember) => manages && m.user_id !== me.id && !(m.role === 'owner' && owners <= 1)
  const remove = async (m: TeamMember) => {
    setBusy(true)
    setError(null)
    try {
      await teamApi.removeMember(team.id, m.user_id)
    } catch (err) {
      setError(err)
    }
    setBusy(false)
    onChanged()
  }
  return (
    <div className="rounded-lg border bg-card px-4 py-3">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
        <Users className="size-4 flex-none text-brand-hover" />
        <span className="min-w-0 truncate font-medium">{team.display_name}</span>
        {manages ? <ToneBadge tone="ok">관리</ToneBadge> : null}
        <span className="ml-auto text-[12px] text-muted-foreground">멤버 {team.members.length}명</span>
      </div>
      <ul className="mt-2 border-t text-[13px]">
        {[...team.members].sort(byTeamRole).map((m) => (
          <li key={m.user_id} className={MEMBER_ROW}>
            <span className="font-medium">{m.username}</span>
            <span className="text-[12px] text-muted-foreground">{teamRoleWord(m.role)}</span>
            {removable(m) ? (
              <button
                type="button"
                className={`ml-auto ${ACTION} ${ACTION_DANGER}`}
                disabled={busy}
                aria-label={`${m.username} 빼기`}
                onClick={() => void remove(m)}
              >
                빼기
              </button>
            ) : null}
          </li>
        ))}
      </ul>
      <Err error={error} />
      {manages ? <Inviting team={team} /> : null}
    </div>
  )
}

/** 관리자의 초대 칸과 답을 기다리는 초대. 그 팀의 관리자에게만 선다 — 다른 사람은 읽을 수 없다(403). */
function Inviting({ team }: { team: Team }) {
  const waiting = usePoll(() => teamApi.invitations(team.id), TEAM_INVITES_POLL_MS, [team.id])
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const trimmed = name.trim()
  const id = `invite-${team.id}`

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!trimmed) return
    setBusy(true)
    setError(null)
    try {
      await teamApi.invite(team.id, trimmed)
      setName('')
    } catch (err) {
      setError(err)
    }
    setBusy(false)
    waiting.refresh()
  }
  const cancel = async (inv: TeamInvitation) => {
    setBusy(true)
    setError(null)
    try {
      await teamApi.cancelInvitation(team.id, inv.id)
    } catch (err) {
      setError(err)
    }
    setBusy(false)
    waiting.refresh()
  }

  return (
    <div className="mt-2 grid gap-2 border-t pt-3">
      <form onSubmit={submit} className="flex flex-wrap items-end gap-2">
        <div className="grid min-w-0 flex-1 basis-48 gap-1.5">
          <Label htmlFor={id}>초대할 아이디</Label>
          {/* 아이디는 그대로 맞춰 본다 — 전화 자판이 첫 글자를 대문자로 바꾸면 있는 사람도 없다고 듣는다. */}
          <Input
            id={id}
            value={name}
            autoComplete="off"
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
            disabled={busy}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <Button type="submit" disabled={busy || !trimmed}>
          초대
        </Button>
      </form>
      <Err error={error} reasons={INVITE_REASONS} />
      <Err error={waiting.error} />
      {(waiting.data ?? []).length > 0 ? (
        <ul className="text-[13px]">
          {(waiting.data ?? []).map((inv) => (
            <li key={inv.id} className={MEMBER_ROW}>
              <span className="font-medium">{inv.username}</span>
              <span className="text-[12px] text-muted-foreground">초대 대기</span>
              <button
                type="button"
                className={`ml-auto ${CHIP}`}
                disabled={busy}
                aria-label={`${inv.username} 초대 거두기`}
                onClick={() => void cancel(inv)}
              >
                거두기
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}
